import { type FC } from 'react';
import { type DataTableCellIndexCoordinate } from '@/components/UI/DataTableV2/DataTable.model';
import { _isNil } from '@/littledash';
import cn from 'classnames';
import styles from '@/components/UI/DataTableV2/DataTableV2.module.scss';
import Tooltip from '@/components/UI/Tooltip';
import { DateTimeRenderer } from '@/components/UI/DateRenderers/DateRenderers';
import { useDataTable } from '@/components/UI/DataTableV2/DataTableProvider';
import { useCellCoordinate } from '@/components/UI/DataTableV2/hooks/useCellCoordinate';
import { useCellData } from '@/components/UI/DataTableV2/hooks/useCellData';
import { useCellPositioning } from '@/components/UI/DataTableV2/hooks/useCellPositioning';
import { useCellSelectionState } from '@/components/UI/DataTableV2/hooks/useCellSelectionState';
import { useCellState } from '@/components/UI/DataTableV2/hooks/useCellState';

type SampleDoseTimeCellProps = {
  coordinate: DataTableCellIndexCoordinate;
  positionOffset: { x: number; y: number };
};

/**
 * Pure display cell for sample dose-time datetime columns.
 *
 * Renders the datetime value (or "+ Record" placeholder when empty) with no
 * editing affordances. Editing is handled by the centralized SingleCellEditor via
 * DateCellEditor.
 */
export const SampleDoseTimeCell: FC<SampleDoseTimeCellProps> = ({ coordinate, positionOffset }) => {
  const { dataTableService } = useDataTable();

  const { idCoordinate, cellRef } = useCellCoordinate(coordinate);
  const { cellData } = useCellData(idCoordinate, cellRef);
  const { cellState, tooltip } = useCellState(idCoordinate, cellRef);
  const { selectionState } = useCellSelectionState(coordinate, idCoordinate);
  const { containerStyle } = useCellPositioning(positionOffset);

  const hasGroupBorder = dataTableService.rightBorderType(coordinate.column) === 'group-separator';

  return (
    <div
      className={cn(styles.CellContainer, { [styles.groupBorder]: hasGroupBorder })}
      style={containerStyle}
      data-cell-coordinate={`${coordinate.column},${coordinate.row}`}
    >
      {cellState === 'Loading' ? (
        <div className={cn(styles.Cell, styles.Loading)} />
      ) : (
        <Tooltip render={tooltip}>
          <div className={cn(styles.Cell, styles[selectionState], styles[cellState])} data-state={cellState}>
            <span className={styles.TimestampValue}>
              {_isNil(cellData?.value) ? (
                <span className="link blue">+ Record</span>
              ) : (
                <DateTimeRenderer value={cellData.value} defaultResponse={cellData.value} />
              )}
            </span>
          </div>
        </Tooltip>
      )}
    </div>
  );
};
